import { Alert, Box, Button, Heading, Icon, Modal, Paragraph, TextButton, ToggleCard } from '@zillow/constellation-10';
import { IconMonitorFilled, IconSignatureOutline } from '@zillow/constellation-icons';
import { useSatelliteWebLandlordLeadManagementHandler_markAsTenant } from '@zg-rentals/ts-satellite-client-react-query';
import Image from 'next/image';
import { useAuth } from '../../../AuthProvider';
import { useInvalidateLeadManagementTable } from '../../../hooks/useLeadManagementTableQuery';
import {
  CLICK_CONTINUE_IN_LEASE_OPTIONS_MODAL_EVENT_TYPE_ID,
  emitClickContinueInLeaseOptionsModalEvent,
  LEASE_OPTION_TRIGGER_SOURCE,
} from '../../../constants/clickstreamEvents/leaseOptionsModal';
import type { LeaseOptionTriggerSource } from '../../../constants/clickstreamEvents/leaseOptionsModal';
import { useRouter } from 'next/router';
import { type ReactNode, useContext, useState } from 'react';
import welcomeHomeImage from '../../../images/welcome-home.svg';
import { AppContext } from '../../../contexts/AppContextProvider';

export const LEASE_OPTIONS: Array<{
  triggerSource: LeaseOptionTriggerSource;
  icon: ReactNode;
  title: string;
  description: string;
}> = [
  {
    triggerSource: LEASE_OPTION_TRIGGER_SOURCE.SEND_THROUGH_ZILLOW,
    icon: <IconMonitorFilled />,
    title: 'Invite and share a lease through Zillow',
    description: 'Upload your preferred version or store a signed lease for your records.',
  },
  {
    triggerSource: LEASE_OPTION_TRIGGER_SOURCE.SEND_SEPARATELY,
    icon: <IconSignatureOutline />,
    title: 'Invite and send a lease separately',
    description: "Send a lease outside Zillow. We'll add the tenant to your property for your records.",
  },
];

type LeaseOptionsModalProps = {
  conversationId: string;
  renterName: string;
  streetAddress: string;
  listingAlias: string;
  leadId?: string | null;
  onClose: () => void;
};

export default function LeaseOptionsModal({
  conversationId,
  renterName,
  streetAddress,
  listingAlias,
  leadId,
  onClose,
}: LeaseOptionsModalProps) {
  const router = useRouter();
  const { paths } = useContext(AppContext);
  const { user } = useAuth();
  const [selectedOption, setSelectedOption] = useState<LeaseOptionTriggerSource | null>(null);
  const [hasError, setHasError] = useState(false);
  const isShareThroughZillow = selectedOption === LEASE_OPTION_TRIGGER_SOURCE.SEND_THROUGH_ZILLOW;
  const invalidateLeadManagementTable = useInvalidateLeadManagementTable();
  const { mutateAsync: markAsTenant, isPending } = useSatelliteWebLandlordLeadManagementHandler_markAsTenant({
    meta: {
      context: { api: 'markAsTenant', details: 'Mark lead as tenant from lease options modal' },
    },
  });

  const handleOptionSelect = (option: LeaseOptionTriggerSource) => {
    setSelectedOption(option);
    setHasError(false);
  };

  const handleContinue = async () => {
    if (selectedOption) {
      emitClickContinueInLeaseOptionsModalEvent({
        user,
        listing: { listingAlias },
        triggerSourceName: selectedOption,
        lead: { leadId },
      });
    }
    if (isShareThroughZillow) {
      router.push(paths.createLeasePage(listingAlias));
      return;
    }
    try {
      const response = await markAsTenant({ postData: { conversationId } });
      if (!response.success) {
        setHasError(true);
        return;
      }
      invalidateLeadManagementTable();
      onClose();
    } catch {
      setHasError(true);
    }
  };

  return (
    <Modal
      open={true}
      onOpenChange={(isOpen) => !isOpen && !isPending && onClose()}
      dividers={false}
      size={{ base: 'fullScreen', md: 'lg' }}
      body={
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
          <Image src={welcomeHomeImage} alt="" width={144} height={144} />
          <Heading level={2} textStyle="heading-xs">
            You&apos;re inviting <Box css={{ display: 'inline', color: 'text.accent.blue.hero' }}>{renterName}</Box> to
            move in to <Box css={{ display: 'inline', color: 'text.accent.blue.hero' }}>{streetAddress}</Box>
          </Heading>
          <Paragraph textStyle="body">
            The next step is to send them a lease. Choose how you&apos;d like to do this.
          </Paragraph>
          <Box css={{ display: 'flex', flexDirection: { base: 'column', md: 'row' }, gap: 'tight', width: '100%' }}>
            {LEASE_OPTIONS.map((option) => {
              const isSelected = selectedOption === option.triggerSource;
              return (
                <ToggleCard
                  key={option.triggerSource}
                  outlined
                  elevated={false}
                  selected={isSelected}
                  onClick={() => handleOptionSelect(option.triggerSource)}
                  css={{
                    textAlign: 'left',
                    flex: 1,
                  }}
                >
                  <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 'tightest' }}>
                    <Box
                      css={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '44px',
                        height: '44px',
                        flexShrink: 0,
                        borderRadius: 'obj.soft',
                        backgroundColor: 'bg.softest',
                        marginBottom: 'tight',
                      }}
                    >
                      <Icon size="md">{option.icon}</Icon>
                    </Box>
                    <Paragraph textStyle="body-bold">{option.title}</Paragraph>
                    <Paragraph textStyle="body">{option.description}</Paragraph>
                  </Box>
                </ToggleCard>
              );
            })}
          </Box>
          <Alert tone="warning">
            Inviting {renterName} to move in will prevent you from inviting other applicants to move in, unless you
            reject or archive {renterName}.
          </Alert>
          {hasError && <Alert tone="critical">We couldn&apos;t process your selection. Please try again.</Alert>}
        </Box>
      }
      footer={
        <Box css={{ display: 'flex', justifyContent: 'flex-end', gap: 'tight' }}>
          <TextButton onClick={onClose} disabled={isPending}>
            Cancel
          </TextButton>
          <Button
            data-eventid={CLICK_CONTINUE_IN_LEASE_OPTIONS_MODAL_EVENT_TYPE_ID}
            emphasis="filled"
            disabled={!selectedOption || isPending}
            onClick={handleContinue}
          >
            Continue
          </Button>
        </Box>
      }
    />
  );
}
